import { Component, OnDestroy, ViewContainerRef, ViewChild, ComponentRef, Compiler, Injector, NgModuleRef, NgModule, Input, EventEmitter, Output } from '@angular/core';
import { MaterialModule } from "../../../material.module";
import { FlexLayoutModule } from "@angular/flex-layout";
import { CommunicationService } from '../../../services/communication/communication.service';
import { DatePipe } from '../../../util/pipes/date.pipe';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { CustomTemplateInputComponent } from './custom-template-input/custom-template-input.component';
import { HttpService } from '../../../services/http/http.service';
import { HelperService } from '../../../util/helper/helper.service';
/**
 * This component used to create the dynamic template and calls the custom input popup
 */
@Component({
  selector: 'app-template',
  template: `<div #printTemplate></div>`
})
export class TemplateComponent implements OnDestroy {

  @ViewChild('printTemplate', {read: ViewContainerRef}) template: ViewContainerRef;
  @Input() htmlText: string;
  @Input() customInputs:any = [];
  @Output() isCustomInputsProvided = new EventEmitter();
  @Output() showLoader = new EventEmitter();
  @Output() onLoad = new EventEmitter();

  templateData = {};
  private cmpRef: ComponentRef<any>;

  constructor(private compiler: Compiler,
              private injector: Injector,
              private dialog: MatDialog,
              private httpService: HttpService,
              private helper: HelperService,
              private communicationService: CommunicationService,
              private moduleRef: NgModuleRef<any>
              ) {
                this.communicationService.getTemplateData.subscribe(templateData=>{
                  this.templateData = templateData;
                })
              }

  ngAfterViewInit() {
    this.openCustomInputPopup()
    setTimeout(x=>{
      const element = document.getElementById('printSection');
      if(element){
        const elements: Element[] = Array.from(element.querySelectorAll("ng-component"));
        elements.forEach((item, index)=> item.setAttribute("id", `printTemplate${index}`));
      }
    })
  }

/**
 * This function will check is there are any custom inputs. If there are any custom inputs then it will open the custom input popup
 */
  openCustomInputPopup(){
    const questions = this.customInputs&&this.customInputs.filter(x=> !x.answer && x.type !== 'api_call') || [];
    if(questions.length){
      const dialogRef = this.dialog.open(CustomTemplateInputComponent, {
        width: '600px',
        maxHeight: '95vh',
        panelClass: 'custom-dialog-container',
        disableClose: true,
        data: {
          questions,
          rowQuestions: this.customInputs
        }
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if(result&&result.questions.length){
          this.getCustomInputAnswers(this.customInputs);
          this.extraInputs().then(res=>{
            this.getCustomInputAnswers(this.customInputs); // Bind the answers after API call 
            this.createComponentFromRaw(this.htmlText, this.templateData, this.onLoad);
            this.isCustomInputsProvided.emit(true);
          });
        }else{
          this.isCustomInputsProvided.emit(false);
        }
      });
    }else if(this.customInputs&&this.customInputs.length){
      this.getCustomInputAnswers(this.customInputs);
      this.extraInputs().then(res=>{
        this.getCustomInputAnswers(this.customInputs); // Bind the answers after API call 
        this.createComponentFromRaw(this.htmlText, this.templateData, this.onLoad);
        this.isCustomInputsProvided.emit(true);
      });
    }else{
      this.createComponentFromRaw(this.htmlText, this.templateData, this.onLoad);
      this.isCustomInputsProvided.emit(true);
    }
  }

/**
 * This function will check whether there is an API call in the template if so then it will call the API
 */
  extraInputs(){
    return new Promise(resolve=>{
      const index = this.customInputs.findIndex(x=> x.type === 'api_call');
      if(index !== -1){
        this.customInputs.map(x=>{
          if(x.type === 'api_call'){
            this.showLoader.emit(true);
            const payload = JSON.parse(this.helper.parseTemplate(JSON.stringify(x.data), this.templateData));
            if(x.method === 'get'){
              this.httpService.get(x.apiUrl).subscribe(res=>{
                x.answer = res[x.responseKey]
                this.showLoader.emit(false);
                resolve(true)
              })
            }else if(x.method === 'post'){
              this.httpService.post(x.apiUrl, payload).subscribe(res=>{
                x.answer = res[x.responseKey];
                this.showLoader.emit(false);
                resolve(true)
              })
            }else if(x.method === 'put'){
              this.httpService.put(x.apiUrl, payload).subscribe(res=>{
                x.answer = res[x.responseKey];
                this.showLoader.emit(false);
                resolve(true)
              })
            }
          }
        })
      }else{
        resolve(true)
      }
    })
  }

/**
 * This will bind the answer which has been given for custom inputs
 * @param questions 
 */
  getCustomInputAnswers(questions){
    const customData = {};
    questions.map(x=> { customData[x.key]= x.answer});
    this.communicationService.setTemplateData(customData, 'custom');
  }

  /**
   * This function will create dynamic module and component based on given template and input values
   * @param template 
   * @param templateData 
   */
  private createComponentFromRaw(template: string, templateData, onLoad) {
    const templateComponent = Component({ template })(class {
      patient = templateData.patient;
      assessment = templateData.assessment;
      documents = templateData.documents;
      tenant = environment.tenant;
      custom = templateData.custom;
      // ngOnInit() {}
      ngAfterViewInit() {
        onLoad.emit(true);
      }
    });

    const tmpModule = NgModule({
      imports: [MaterialModule, FlexLayoutModule, CommonModule],
      providers: [DatePipe],
      declarations: [templateComponent, DatePipe],
    })(class {});

    this.compiler.compileModuleAndAllComponentsAsync(tmpModule)
      .then(factories => {
        const f = factories.componentFactories[0];
        this.cmpRef = f.create(this.injector, [], null, this.moduleRef);
        this.cmpRef.instance.name = 'print-template';
        this.template.insert(this.cmpRef.hostView);
      });
  }

  ngOnDestroy() {
    if(this.cmpRef) {
      this.cmpRef.destroy();
    }
  }

  /**
   * Custom input question example
   * 
   * [{"title":"Enter total amount?","placeholder":"Enter amount","button_text":"Continue","validations":{"required":true},"type":"number","key":"totalAmount","answer":""},{"title":"Enter a short description?","placeholder":"Enter description","button_text":"Submit","validations":{},"type":"short_text","key":"description","answer":""},{"type": "api_call", "apiUrl": "message/shorten", "key": "bitlyUrl","responseKey":"url", "method":"post", "data": {"url":"https://form.jotform.com/202186597578067","params":[{"name":"he_pid","value":"{{patient.id}}","encrypt":true},{"name":"h_amount","value":"{{custom.totalAmount}}","encrypt":false},{"name":"he_amount","value":"{{custom.totalAmount}}","encrypt":true}]}}]
   * 
   * To make your check in touchless, pay your office visit fees online using the link below. Charges today: ${{custom.totalAmount}}\nCharge description: {{custom.description}}.\n{{custom.bitlyUrl}}
   */

}
